import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compose a natural review paragraph from the checked statements + free text.
function compose(selected, freeText) {
  const parts = [];
  if (selected.length) {
    parts.push(selected.map((s) => s.replace(/\.$/, '')).join('. ') + '.');
  }
  const ft = (freeText || '').trim();
  if (ft) parts.push(ft);
  return parts.join(' ');
}

export default function ReviewPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [freeText, setFreeText] = useState('');
  const [rating, setRating] = useState(5);
  const [manualText, setManualText] = useState(null); // if the user edits the textarea
  const [customMode, setCustomMode] = useState(false); // write-your-own vs suggestions
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { google_url, trustpilot_url, composed_text }

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/reviews/public/${token}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        if (res.data.already_submitted) {
          setDone({
            google_url: res.data.google_url,
            trustpilot_url: res.data.trustpilot_url,
            composed_text: res.data.composed_text,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.status === 404 ? 'Ce lien est invalide ou a expiré.' : 'Une erreur est survenue.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const accent = data?.brand_accent || '#059669';
  const autoText = useMemo(() => compose(selected, freeText), [selected, freeText]);
  const composedText = manualText != null ? manualText : autoText;

  const toggle = (s) => {
    setManualText(null);
    setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const submit = async () => {
    if (!composedText.trim()) { setError('Sélectionnez au moins un point positif.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/reviews/public/${token}/submit`, {
        selected_statements: selected,
        free_text: freeText,
        rating,
        composed_text: composedText,
      });
      setDone(res.data);
    } catch {
      setError("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const publish = async (url) => {
    try { await navigator.clipboard.writeText(done?.composed_text || composedText); } catch { /* ignore */ }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ── States ──────────────────────────────────────────────────────────────
  if (loading) {
    return <Shell accent="#059669"><p className="text-center text-slate-500 py-10">Chargement…</p></Shell>;
  }
  if (error && !data) {
    return <Shell accent="#059669"><p className="text-center text-red-600 py-10">{error}</p></Shell>;
  }

  if (done) {
    const g = done.google_url, t = done.trustpilot_url;
    return (
      <Shell accent={accent} logo={data?.logo} label={data?.brand_label}>
        <div className="text-center py-4">
          <div className="text-5xl mb-3">🙏</div>
          <h2 className="text-xl font-bold text-slate-900 mb-1">Merci beaucoup !</h2>
          <p className="text-slate-600 text-sm mb-5">
            {(g || t)
              ? 'Votre avis a bien été enregistré.'
              : 'Votre retour a bien été enregistré — il nous aide à nous améliorer.'}
          </p>
          {(g || t) && (
            <>
              <p className="text-sm text-slate-700 mb-3">
                Si vous avez 30 secondes, votre avis aiderait beaucoup d'autres personnes — publiez-le en 1 clic (il est déjà copié) 👇
              </p>
              <div className="flex flex-col gap-2">
                {g && (
                  <button onClick={() => publish(g)} className="w-full rounded-xl px-5 py-3 font-bold text-white" style={{ background: accent }}>
                    Publier sur Google
                  </button>
                )}
                {t && (
                  <button onClick={() => publish(t)} className="w-full rounded-xl px-5 py-3 font-bold text-white" style={{ background: accent }}>
                    Publier sur Trustpilot
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell accent={accent} logo={data?.logo} label={data?.brand_label}>
      <p className="text-slate-700 text-[15px] mb-1">
        Bonjour <strong>{data?.contact_name || data?.client_nom}</strong>,
      </p>
      <p className="text-slate-600 text-sm mb-4">
        Cochez ce qui vous a plu — votre avis se rédige tout seul. Vous pourrez le modifier avant de le publier.
      </p>

      {data?.equipment_names?.length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-4">
          <p className="text-xs text-slate-500 mb-1">Votre équipement</p>
          {data.equipment_names.map((n, i) => (
            <p key={i} className="text-sm font-semibold text-slate-800">{n}</p>
          ))}
        </div>
      )}

      {/* Rating */}
      <div className="flex items-center justify-center gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} className="text-3xl leading-none" aria-label={`${n} étoiles`}>
            <span style={{ color: n <= rating ? '#fbbf24' : '#e2e8f0' }}>★</span>
          </button>
        ))}
      </div>

      {/* Mode toggle: suggestions vs fully custom review */}
      <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg text-sm">
        <button
          onClick={() => { setCustomMode(false); setManualText(null); }}
          className={`flex-1 rounded-md py-1.5 font-medium transition-colors ${!customMode ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
        >
          À partir des suggestions
        </button>
        <button
          onClick={() => { setCustomMode(true); setSelected([]); setFreeText(''); setManualText(''); }}
          className={`flex-1 rounded-md py-1.5 font-medium transition-colors ${customMode ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
        >
          ✍️ Écrire mon avis
        </button>
      </div>

      {!customMode && (
        <>
          {/* Statements */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(data?.statements || []).map((s, i) => {
              const on = selected.includes(s);
              return (
                <button
                  key={i}
                  onClick={() => toggle(s)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? 'text-white font-medium' : 'text-slate-600 hover:border-slate-400'}`}
                  style={on ? { background: accent, borderColor: accent } : {}}
                >
                  {on ? '✓ ' : ''}{s}
                </button>
              );
            })}
          </div>

          {/* Free text */}
          <input
            value={freeText}
            onChange={(e) => { setManualText(null); setFreeText(e.target.value); }}
            placeholder="Ajouter un mot personnel (optionnel)…"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-4"
          />
        </>
      )}

      {/* Review text — editable (or fully written by the client in custom mode) */}
      <label className="text-xs text-slate-500">{customMode ? 'Votre avis' : 'Votre avis (modifiable)'}</label>
      <textarea
        value={composedText}
        onChange={(e) => setManualText(e.target.value)}
        rows={customMode ? 5 : 4}
        placeholder={customMode ? 'Rédigez votre avis en quelques mots…' : 'Cochez des points ci-dessus…'}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1 mb-4"
        autoFocus={customMode}
      />

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <button
        onClick={submit}
        disabled={submitting || !composedText.trim()}
        className="w-full rounded-xl px-5 py-3 font-bold text-white disabled:opacity-50"
        style={{ background: accent }}
      >
        {submitting ? 'Envoi…' : 'Valider mon avis'}
      </button>
    </Shell>
  );
}

function Shell({ accent, logo, label, children }) {
  return (
    <div className="min-h-screen py-8 px-4" style={{ background: `${accent}12` }}>
      <div className="max-w-lg mx-auto">
        <div className="rounded-2xl overflow-hidden bg-white shadow-xl">
          <div className="px-6 py-7 text-center" style={{ background: accent }}>
            {logo ? (
              <img src={logo} alt={label || ''} style={{ maxHeight: 44, filter: 'brightness(0) invert(1)' }} className="mx-auto" />
            ) : (
              <div className="text-white text-xl font-extrabold">{label || 'Votre avis'}</div>
            )}
            <p className="text-white/95 font-semibold mt-2 text-sm">Votre avis compte ⭐</p>
          </div>
          <div className="p-6">{children}</div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">LDWS GROUPE</p>
      </div>
    </div>
  );
}
